'use client'
import { Carousel } from 'flowbite-react'
import { useState } from 'react'
import { SmartLink } from '@/payload-types'
import Link from 'next/link'
import ImageComponent from '../media/imageComponent'
import { SnippetComponent } from '../snippets/snippet'
import PopupComponent from '../popup/popup'

interface IProps {
  text?: (string | undefined)[] | undefined
  smartLink: SmartLink
}

export const SmartLinksComponent = ({ text, smartLink }: IProps) => {
  const customCarouselTheme = {
    root: {
      base: 'relative h-full w-full',
      leftControl:
        'font-bold absolute -left-1 top-0 flex h-full items-center justify-center focus:outline-0',
      rightControl:
        'font-bold absolute -right-1 top-0 flex h-full items-center justify-center focus:outline-0',
    },
    indicators: {
      active: {
        off: 'bg-gray-400 hover:bg-gray-700 dark:bg-gray-800/50 dark:hover:bg-gray-800 group-focus:outline-0',
        on: 'bg-gray-600 dark:bg-gray-800',
      },
      base: 'h-2 w-2 rounded-full',
      wrapper: 'absolute bottom-5 left-1/2 flex -translate-x-1/2 space-x-3',
    },
    item: {
      base: 'absolute left-1/2 top-1/2 block w-full -translate-x-1/2 -translate-y-1/2',
      wrapper: {
        off: 'w-full flex-shrink-0 transform cursor-default snap-center',
        on: 'w-full flex-shrink-0 transform cursor-grab snap-center',
      },
    },
    control: {
      base: 'inline-flex items-center justify-center rounded-full group-focus:outline-0 h-6 w-6 rounded-full bg-gray-200 opacity-60 hover:bg-gray-300',
      icon: 'text-gray-600 h-4 w-4 font-extrabold !stroke-[3px]',
    },
    scrollContainer: {
      base: 'flex h-full',
      snap: 'snap-x',
    },
  }

  const payload_url = process.env.NEXT_PUBLIC_PAYLOAD_URL ? process.env.NEXT_PUBLIC_PAYLOAD_URL : ''
  const NUM_IMAGES = smartLink.screenshot ? smartLink.screenshot.length : 0

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [isSnippetOpen, setIsSnippetOpen] = useState(true)

  const handleImageClick = (imageSrc: string) => {
    setSelectedImage(imageSrc)
    setIsPopoverOpen(true)
    setIsSnippetOpen(false)
  }

  const handlePopoverClose = () => {
    setIsPopoverOpen(false)
    setIsSnippetOpen(true)
  }

  return (
    <>
      {!isSnippetOpen && <span className="font-bold">{smartLink.textToDisplay}</span>}
      {smartLink && smartLink.textToDisplay && isSnippetOpen && (
        <SnippetComponent
          text={text}
          css={'border border-black rounded-lg w-[316px] z-50 bg-white'}
        >
          <span className="block smartLinks px-3 py-2 text-sm">
            {NUM_IMAGES > 1 ? (
              <span className="block">
                <span className="block h-[230px]">
                  <Carousel
                    theme={customCarouselTheme}
                    slide={false}
                    indicators={true}
                    className="!border-0"
                  >
                    {smartLink.screenshot?.map((item, index) => (
                      <button
                        className="block pt-1 pb-2 h-[230px]"
                        key={index}
                        onClick={() =>
                          handleImageClick(
                            `${payload_url}${
                              typeof item === 'object' ? item.sizes?.webp?.url : ''
                            }`,
                          )
                        }
                      >
                        <ImageComponent
                          src={`${payload_url}${
                            typeof item === 'object' ? item.sizes?.webp?.url : ''
                          }`}
                          alt="image"
                        />
                      </button>
                    ))}
                  </Carousel>
                </span>
              </span>
            ) : (
              NUM_IMAGES != 0 &&
              smartLink.screenshot?.map((item, index) => (
                <span key={index} className="max-w-full flex pt-1 pb-2 h-[230px] justify-center">
                  <button
                    className="block pt-1 pb-2 h-[230px]"
                    key={index}
                    onClick={() =>
                      handleImageClick(
                        `${payload_url}${typeof item === 'object' ? item.sizes?.webp?.url : ''}`,
                      )
                    }
                  >
                    <ImageComponent
                      src={`${payload_url}${typeof item === 'object' ? item.sizes?.webp?.url : ''}`}
                      alt="image"
                    />
                  </button>
                </span>
              ))
            )}
            {smartLink.externalLink && (
              <span className="break-all line-clamp-1">
                <span className="font-medium text-gray-600">original link:</span>
                {'  '}
                <Link
                  className="text-blue-700 hover:underline"
                  href={smartLink.externalLink}
                  target="_blank"
                >
                  {smartLink.externalLink}
                </Link>
              </span>
            )}
            {smartLink.archiveLink && (
              <span className="break-all line-clamp-1">
                <span className="font-medium text-gray-600">archive link:</span>
                {'  '}
                <Link
                  className="text-blue-700 hover:underline"
                  href={smartLink.archiveLink}
                  target="_blank"
                >
                  {smartLink.archiveLink}
                </Link>
              </span>
            )}
          </span>
        </SnippetComponent>
      )}

      {isPopoverOpen && selectedImage && (
        <PopupComponent onClose={handlePopoverClose}>
          <ImageComponent src={selectedImage} alt="Selected image" />
        </PopupComponent>
      )}
    </>
  )
}

export default SmartLinksComponent
